import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { AlertCircle, CheckCircle2, Shield, Truck } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

export function ProductInfo({ product }) {
  return (
    <div className="space-y-8">
      <Tabs defaultValue="description" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="description">產品說明</TabsTrigger>
          <TabsTrigger value="ingredients">成分資訊</TabsTrigger>
          <TabsTrigger value="usage">食用方式</TabsTrigger>
          <TabsTrigger value="warnings">注意事項</TabsTrigger>
          <TabsTrigger value="faq">常見問題</TabsTrigger>
        </TabsList>
        
        {/* 產品說明 */}
        <TabsContent value="description" className="space-y-6">
          <Card>
            <CardContent className="pt-6">
              <h3 className="text-xl font-semibold mb-4">產品特色</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {product.features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <span className="text-3xl">{feature.icon}</span>
                    <div>
                      <h4 className="font-semibold">{feature.title}</h4>
                      <p className="text-sm text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <h3 className="text-xl font-semibold mb-4">產品說明</h3>
              <p className="text-gray-700 leading-relaxed">{product.description}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <h3 className="text-xl font-semibold mb-4">產品規格</h3>
              <div className="space-y-3">
                <div className="flex justify-between border-b pb-2">
                  <span className="text-gray-600">內容量</span>
                  <span className="font-medium">{product.specifications.netContent}</span>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <span className="text-gray-600">建議用量</span>
                  <span className="font-medium">{product.specifications.servingSize}</span>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <span className="text-gray-600">保存方式</span>
                  <span className="font-medium">{product.specifications.storage}</span>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <span className="text-gray-600">原產地</span>
                  <span className="font-medium">{product.specifications.origin}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* 成分資訊 */}
        <TabsContent value="ingredients" className="space-y-6">
          <Card>
            <CardContent className="pt-6">
              <h3 className="text-xl font-semibold mb-4">主要成分</h3>
              <ul className="space-y-2">
                {product.ingredients.map((ingredient, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{ingredient}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <h3 className="text-xl font-semibold mb-4">不含成分（過敏原資訊）</h3>
              <p className="text-sm text-gray-600 mb-4">本產品不含以下成分：</p>
              <div className="flex flex-wrap gap-2">
                {product.allergenFree.map((item, index) => (
                  <Badge key={index} variant="outline" className="text-sm">
                    {item}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* 食用方式 */}
        <TabsContent value="usage" className="space-y-6">
          <Alert>
            <CheckCircle2 className="h-4 w-4" />
            <AlertTitle>建議食用方式</AlertTitle>
            <AlertDescription>
              {product.specifications.servingSize}，{product.specifications.usage}
            </AlertDescription>
          </Alert>
          
          <Card>
            <CardContent className="pt-6">
              <h3 className="text-xl font-semibold mb-4">食用建議</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                  <span>建議早晨空腹或餐後食用，吸收效果更佳</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                  <span>搭配充足水分（約 200-300ml）食用</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                  <span>建議固定時段食用，養成規律習慣</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                  <span>配合均衡飲食與規律運動，效果更顯著</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* 注意事項 */}
        <TabsContent value="warnings" className="space-y-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>重要提醒</AlertTitle>
            <AlertDescription>
              本產品屬一般食品，非藥品，非用於預防、診斷、治療或矯正任何疾病。
            </AlertDescription>
          </Alert>
          
          <Card>
            <CardContent className="pt-6">
              <h3 className="text-xl font-semibold mb-4">使用注意事項</h3>
              <ul className="space-y-3">
                {product.warnings.map((warning, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{warning}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <h3 className="text-xl font-semibold mb-4">保存方式</h3>
              <p className="text-gray-700">{product.specifications.storage}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <Shield className="h-6 w-6 text-blue-600 mt-1" />
                <div>
                  <h3 className="text-xl font-semibold mb-2">品質保證</h3>
                  <p className="text-gray-700 mb-2">{product.supplier.name}</p>
                  <p className="text-sm text-gray-600">
                    食品業登錄字號：{product.supplier.registrationNumber}
                  </p>
                  <p className="text-sm text-gray-600">
                    {product.supplier.insurance}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* 常見問題 */}
        <TabsContent value="faq" className="space-y-6">
          <Accordion type="single" collapsible className="w-full">
            {product.faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-700">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <Truck className="h-6 w-6 text-blue-600 mt-1" />
                <div>
                  <h3 className="text-xl font-semibold mb-2">運送與退換貨</h3>
                  <p className="text-gray-700 mb-3">
                    {product.shipping.note}，預計 {product.shipping.estimatedDays} 送達。
                  </p>
                  <p className="text-sm text-gray-600 mb-2">
                    退換貨期限：收到商品後 {product.returnPolicy.period} 天內
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {product.returnPolicy.conditions.map((condition, index) => (
                      <li key={index}>• {condition}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
